<template>
  <filtered-table
    :rows="authorizationsEnriched"
    :columns="columns"
    :default-columns="defaultColumns"
    :pagination="pagination"
    :loading="isLoading > 0"
    :no-data-label="$t('authorizations.authorizedNoData')"
    data-e2e="table-authorized-services"
    disable-search
    disable-export
    disable-views
    flat
    @row-click="onEditAuthorization"
  >
    <template #top>
      <div class="text-h6">
        {{ $t('authorizations.authorizedServices') }}
      </div>
    </template>
    <template #top-menu>
      <div>
        <q-btn
          color="primary"
          icon="add"
          data-e2e="button-add-authorized-service"
          :label="$t('common.buttonAdd')"
          @click="onAddAuthorization"
        />
      </div>
    </template>
    <template #body-cell-authorizationStatus="props">
      <q-td :props="props">
        <universal-tooltip
          show-tooltip
          :text="getAuthorizationStatusLabel(props.row.authorizationStatus)"
        >
          <q-icon
            :name="authorizationStatuses[props.row.authorizationStatus]?.icon"
            :color="authorizationStatuses[props.row.authorizationStatus]?.color"
            :data-e2e="`icon-authorized-service-status-${props.row.authorizationStatus}`"
            size="xs"
          />
        </universal-tooltip>
      </q-td>
    </template>
  </filtered-table>
</template>

<script>
import EditAuthorizedServiceDialog from 'components/Billing/EditAuthorizedServiceDialog';
import FilteredTable from 'components/UI/FilteredTable';
import UniversalTooltip from 'components/UI/UniversalTooltip';
import { Timestamp } from 'firebase/firestore';
import { omit } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useTime } from 'src/composables/time';
import Interactions from 'src/mixins/Interactions';
import { useFundingSourceVersions } from 'src/contexts/revenueCycle';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';

export default {
  name: 'AuthorizedServicesTable',

  components: {
    FilteredTable,
    UniversalTooltip,
  },

  mixins: [Interactions, Toolbox],

  props: {
    authorizations: {
      type: Array,
      required: true,
    },
    hideFilter: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['create-authorizations', 'update-authorization', 'delete-authorization'],

  setup() {
    const { getLocaleDateString } = useTime();

    return {
      // Versioned funders: a raw read returns one document per VERSION, which duplicated every payer in
      // the picker below and made the lookup match the first version forever. The context resolves one
      // per payer as of today.
      fundingSourceVersions: useFundingSourceVersions(),
      getLocaleDateString,
    };
  },

  data() {
    const statusOrder = {
      upcoming: 0,
      active: 1,
      expired: 2,
      unknown: 3,
    };

    return {
      isLoading: false,
      allFundingSources: [],

      pagination: {
        sortBy: 'authorizationStatus',
        descending: false,
        page: 1,
        rowsPerPage: 10,
      },
      columns: [
        {
          name: 'authorizationStatus',
          align: 'left',
          field: 'authorizationStatus',
          label: 'Status',
          sortable: true,
          sort: (a, b, aObj, bObj) => {
            if (a === b) {
              const aStartTimestampStr = aObj.startTimestamp instanceof Timestamp
                ? aObj.startTimestamp.toDate().toISOString()
                : '';
              const bStartTimestampStr = bObj.startTimestamp instanceof Timestamp
                ? bObj.startTimestamp.toDate().toISOString()
                : '';
              return bStartTimestampStr.localeCompare(aStartTimestampStr);
            }
            return (statusOrder[a]) - (statusOrder[b]);
          },
        },
        {
          name: 'fundingSourceName',
          required: true,
          label: this.$t('authorizations.fields.fundingSource'),
          align: 'left',
          field: 'fundingSourceName',
          sortable: true,
        },
        {
          name: 'authorizationNumber',
          align: 'left',
          label: this.$t('authorizations.fields.authorizationNumber'),
          field: 'authorizationNumber',
          sortable: true,
        },
        {
          name: 'startTimestamp',
          align: 'left',
          label: this.$t('authorizations.fields.startTimestamp'),
          field: 'startTimestamp',
          format: (startTimestamp) => {
            return (startTimestamp instanceof Timestamp
              ? this.getLocaleDateString(startTimestamp, {
                options: { year: 'numeric', month: 'numeric', day: 'numeric' },
              }) : ''
            );
          },
          sortable: true,
        },
        {
          name: 'endTimestamp',
          align: 'left',
          label: this.$t('authorizations.fields.endTimestamp'),
          field: 'endTimestamp',
          format: (endTimestamp) => {
            return (endTimestamp instanceof Timestamp
              ? this.getLocaleDateString(endTimestamp, {
                options: { year: 'numeric', month: 'numeric', day: 'numeric' },
              }) : ''
            );
          },
          sortable: true,
        },
        {
          name: 'treatmentServiceName',
          align: 'left',
          label: this.$t('authorizations.fields.serviceName'),
          field: 'treatmentServiceName',
          sortable: true,
        },
        {
          name: 'treatmentServiceCode',
          align: 'left',
          label: this.$t('authorizations.fields.serviceCode'),
          field: 'treatmentServiceCode',
          sortable: true,
        },
      ],
      authorizationStatuses: {
        upcoming: {
          icon: 'hourglass_empty',
          color: 'warning',
        },
        active: {
          icon: 'check_circle',
          color: 'green',
        },
        expired: {
          icon: 'cancel',
          color: 'red',
        },
        unknown: {
          icon: 'help_outline',
          color: 'grey',
        },
      },
    };
  }, // data()

  computed: {
    ...mapState(auth, ['userDetails', 'treatmentServices']),

    authorizationsEnriched() {
      return this.authorizations.map((authorization) => {
        const treatmentService = this.treatmentServices.find((ts) => ts.id === authorization.treatmentService?.id);
        // The PAYER, not a version document — an authorization stores the series id.
        const fundingSource = this.allFundingSources
          .find((fs) => fs.fundingSourceId === authorization.fundingSource);
        // `?? ''` rather than a trailing `|| ''`: the template literal always contains the
        // indentation below, so it is never falsy and the old fallback could not fire. An
        // unresolvable funder rendered the string "undefined" in the column.
        const fundingSourceName = `${fundingSource?.name ?? ''}
          ${fundingSource?.isActive === false ? ` (${this.$t('fundingSourceStatuses.inactive')})` : ''}`;

        return {
          ...authorization,
          fundingSourceName,
          treatmentServiceCode: treatmentService?.serviceCode || '',
          treatmentServiceName: treatmentService?.serviceName || '',
          authorizationStatus: this.getAuthorizationStatus(authorization),
        };
      });
    }, // authorizationsEnriched()

    defaultColumns() {
      return this.columns.map((col) => col.name);
    }, // defaultColumns()

    hideFilterColumns() {
      if (this.hideFilter) {
        return this.columns.map((col) => ({ ...col, filterable: false }));
      }

      return this.columns;
    }, // hideFilterColumns()

    activeFundingSources() {
      return this.allFundingSources.filter((fundingSource) => fundingSource.isActive);
    }, // activeFundingSources()

  }, // computed()

  mounted() {
    // Fire-and-forget: ensureTreatmentServicesBound rejects on a bind error (which it
    // already logs internally), so swallow here to avoid an unhandled rejection. The
    // table reacts to treatmentServices state, so no return value is needed.
    this.ensureTreatmentServicesBound().catch(() => {});
    // loadFundingSources handles its own errors and never rejects.
    this.loadFundingSources();
  }, // mounted()

  methods: {
    ...mapActions(auth, ['ensureTreatmentServicesBound']),

    onAddAuthorization() {
      this.addAuthorizations();
    }, // onAddAuthorization()

    onEditAuthorization(evt, authorization) {
      this.editAuthorization(authorization);
    }, // onEditAuthorization()

    addAuthorizations() {
      this.openComponentDialog(
        EditAuthorizedServiceDialog,
        {
          authorization: {
            fundingSource: null,
            authorizationNumber: null,
            startTimestamp: null,
            endTimestamp: null,
          },
          fundingSources: this.activeFundingSources,
        },
        async ({ authorization, treatmentServices }) => {
          const newAuthorizations = treatmentServices.map((ts) => (
            {
              ...authorization,
              treatmentService: ts,
            }
          ));
          return this.$emit('create-authorizations', newAuthorizations);
        },
      );
    }, // addAuthorizations()

    editAuthorization(authorization) {
      this.openComponentDialog(
        EditAuthorizedServiceDialog,
        {
          authorization,
          fundingSources: this.allFundingSources,
        },
        async ({ authorization: newAuthorization }) => {
          if (newAuthorization?.editStatus === 'delete') {
            return this.$emit('delete-authorization', omit(newAuthorization, ['editStatus']));
          }
          return this.$emit('update-authorization', omit(newAuthorization, [
            'fundingSourceName',
            'treatmentServiceCode',
            'treatmentServiceName',
            'authorizationStatus',
          ]));
        },
      );
    }, // editAuthorization()

    async loadFundingSources() {
      if (this.allFundingSources.length > 0) {
        return;
      }
      this.isLoading += 1;
      try {
        this.allFundingSources = await this.fundingSourceVersions.loadAll({
          clinicId: this.userDetails.clinic,
        });
      } catch (err) {
        this.notifyNegative(this.$t('common.errors.generic'));
      } finally {
        this.isLoading -= 1;
      }
    }, // loadFundingSources()

    getAuthorizationStatus(authorization) {
      const startTimestamp = (authorization.startTimestamp instanceof Timestamp
        ? authorization.startTimestamp.toDate()
        : null);
      const endTimestamp = (authorization.endTimestamp instanceof Timestamp
        ? authorization.endTimestamp.toDate()
        : null);
      const now = new Date();
      if (startTimestamp && startTimestamp > now) {
        return 'upcoming';
      }
      if (endTimestamp && endTimestamp < now) {
        return 'expired';
      }
      return 'active';
    }, // getAuthorizationStatus()

    getAuthorizationStatusLabel(authorizationStatus) {
      switch (authorizationStatus) {
        case 'upcoming':
          return this.$t('authorizations.status.upcoming');
        case 'expired':
          return this.$t('authorizations.status.expired');
        case 'active':
          return this.$t('authorizations.status.active');
        default:
          return this.$t('authorizations.status.unknown');
      }
    }, // getAuthorizationStatusLabel()

  },
};
</script>
